import { getSupabaseClient } from "@/integrations/supabase/client";
import {
    RealtimeChannel,
    RealtimePostgresChangesFilter,
    RealtimePostgresChangesPayload
} from "@supabase/supabase-js";
import { QueryClient, QueryKey } from "@tanstack/react-query";
import { useEffect } from "react";

export type PostgresRealtimeListener = {
    config: RealtimePostgresChangesFilter<'*' | 'INSERT' | 'UPDATE' | 'DELETE'>;
    invalidateKeys?: QueryKey | QueryKey[];
    onPayload?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
};

export type RealTimeSubscriptionProps = {
    channelName: string;
    orgId: string | undefined;
    queryClient: QueryClient;
    listeners: PostgresRealtimeListener[];
};

type SharedChannel = {
    count: number;
    channel: RealtimeChannel;
};

const sharedChannels = new Map<string, SharedChannel>();
const latestListeners = new Map<string, PostgresRealtimeListener[]>();
const pendingRealtimeFetches = new Map<string, QueryKey>();
let pendingRealtimeFetchFrame = 0;

const scheduleRealtimeFetch = (queryClient: QueryClient, queryKey: QueryKey) => {
    pendingRealtimeFetches.set(JSON.stringify(queryKey), queryKey);
    if (pendingRealtimeFetchFrame) return;

    const flush = () => {
        pendingRealtimeFetchFrame = 0;
        const keys = [...pendingRealtimeFetches.values()];
        pendingRealtimeFetches.clear();
        for (const key of keys) {
            void queryClient.fetchQuery({ queryKey: key, staleTime: 0 });
        }
    };

    if (typeof requestAnimationFrame === 'function') {
        pendingRealtimeFetchFrame = requestAnimationFrame(flush);
        return;
    }
    flush();
};

const releaseSharedChannel = (channelName: string, supabase: ReturnType<typeof getSupabaseClient>) => {
    const shared = sharedChannels.get(channelName);
    if (!shared) return;
    shared.count -= 1;
    if (shared.count > 0) return;
    sharedChannels.delete(channelName);
    void supabase.removeChannel(shared.channel);
};

export const useRealTimeSubscription = ({ channelName, orgId, queryClient, listeners }: RealTimeSubscriptionProps) => {
    latestListeners.set(channelName, listeners);

    // Subscribe when orgId/channelName change, not when `listeners` gets a new
    // array identity (every parent render). Handlers read latestListeners on each event.
    useEffect(() => {
        const currentListeners = latestListeners.get(channelName) ?? [];
        if (!orgId || !queryClient || currentListeners.length === 0) return;

        const supabase = getSupabaseClient();
        const existing = sharedChannels.get(channelName);

        if (existing) {
            existing.count += 1;
            return () => releaseSharedChannel(channelName, supabase);
        }

        const channel = supabase.channel(channelName);

        for (const listener of currentListeners) {
            const configRealTime = listener.config as RealtimePostgresChangesFilter<typeof listener.config.event>;

            channel.on('postgres_changes', configRealTime, (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
                const liveListeners = latestListeners.get(channelName) ?? [listener];
                const liveListener = liveListeners.find(
                    (candidate) => candidate.config.table === listener.config.table
                ) ?? listener;
                const keys = liveListener.invalidateKeys
                    ? Array.isArray(liveListener.invalidateKeys) ? liveListener.invalidateKeys : [liveListener.invalidateKeys]
                    : [];

                for (const queryKey of keys) {
                    scheduleRealtimeFetch(queryClient, queryKey);
                }

                if (liveListener.onPayload) liveListener.onPayload(payload);
            });
        }

        sharedChannels.set(channelName, { count: 1, channel });
        channel.subscribe();
        return () => releaseSharedChannel(channelName, supabase);
    }, [orgId, queryClient, channelName]);
};
